'use client';

import { useRouter } from 'next/navigation';
import React, { Fragment, isValidElement, type ReactNode, useCallback, useEffect } from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { isCoarsePointerInteraction } from '@/lib/dom/pointer';
import { asRoute } from '@/lib/routes/app';
import type { UserPreviewVM } from '@/lib/view-models/user-preview';
import { type PreviewWrapperProperties, UserPreviewCard } from './user-preview/UserPreviewCard';
import { useUserPreviewState } from './user-preview/useUserPreviewState';

interface UserPreviewProperties {
  allowNestedHover?: boolean;
  children: ReactNode;
  prefetched?: Partial<UserPreviewVM>;
  profileId?: string | null;
  username: string;
}

export function UserPreview({
  username,
  profileId,
  children,
  prefetched,
  allowNestedHover = false,
}: UserPreviewProperties) {
  const normalizedProfileId = profileId ?? null;
  const previewStateOptions = prefetched
    ? { username, profileId: normalizedProfileId, prefetched }
    : { username, profileId: normalizedProfileId };
  const {
    lookupAvailable,
    hoverCardOpen,
    openDelay,
    shouldRenderCard,
    showFallbackLoading,
    loading,
    error,
    user,
    twitterProfileUrl,
    followState,
    metrics,
    locationIsLinkable,
    bannerReady,
    handleHoverCardOpenChange,
    handlePointerEnter,
    handlePointerLeave,
    handleUnmount,
    formatJoinedAt,
  } = useUserPreviewState(previewStateOptions);

  const router = useRouter();

  useEffect(() => handleUnmount, [handleUnmount]);

  const triggerChild =
    isValidElement(children) && children.type !== Fragment ? (
      children
    ) : (
      <span className="inline-flex items-center gap-1 align-middle">{children}</span>
    );

  const handleTriggerClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (isCoarsePointerInteraction(event) && !hoverCardOpen) {
        event.preventDefault();
        handleHoverCardOpenChange(true);
        return;
      }

      const { button, metaKey, altKey, ctrlKey, shiftKey, defaultPrevented } = event;
      if (defaultPrevented) return;
      if (button !== 0 || metaKey || altKey || ctrlKey || shiftKey) return;

      const element = event.currentTarget;
      if (!(element instanceof HTMLAnchorElement)) return;
      if (element.target && element.target !== '_self') return;

      const hrefAttribute = element.getAttribute('href');
      if (!hrefAttribute) return;

      try {
        const base = globalThis.window === undefined ? 'https://local.invalid' : globalThis.location.origin;
        const resolvedUrl = new URL(hrefAttribute, base);
        if (globalThis.window !== undefined && resolvedUrl.origin !== globalThis.location.origin) {
          return;
        }

        event.preventDefault();
        const pathname = asRoute(resolvedUrl.pathname);
        const queryEntries = [...resolvedUrl.searchParams.entries()];
        const nextPath =
          queryEntries.length > 0 || resolvedUrl.hash
            ? `${pathname}${resolvedUrl.search}${resolvedUrl.hash}`
            : pathname;
        router.push(asRoute(nextPath));
      } catch {
        // ignore malformed URLs
      }
    },
    [handleHoverCardOpenChange, hoverCardOpen, router]
  );

  const previewWrapper = ({
    profileId: wrappedProfileId,
    username: wrappedUsername,
    children: previewChildren,
  }: PreviewWrapperProperties) => {
    if (!wrappedUsername) {
      return <>{previewChildren}</>;
    }

    return (
      <UserPreview profileId={wrappedProfileId ?? null} username={wrappedUsername}>
        {previewChildren}
      </UserPreview>
    );
  };

  if (!lookupAvailable) {
    return <>{children}</>;
  }

  if (children === undefined) {
    return null;
  }

  return (
    <HoverCard
      allowNested={allowNestedHover}
      open={hoverCardOpen}
      openDelay={openDelay}
      closeDelay={100}
      onOpenChange={handleHoverCardOpenChange}
    >
      {/* Forward real pointer enter/leave so the state machine can cancel stale Radix open events. */}
      <HoverCardTrigger
        asChild
        onClick={handleTriggerClick}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
      >
        {triggerChild}
      </HoverCardTrigger>
      <HoverCardContent
        className="w-96 max-h-[600px] overflow-hidden rounded-xl border border-border/70 bg-card text-card-foreground p-0 shadow-xl"
        align="center"
        side="top"
        sideOffset={12}
        collisionPadding={16}
        avoidCollisions
        sticky="partial"
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
      >
        {shouldRenderCard ? (
          <UserPreviewCard
            user={user}
            loading={loading}
            error={error}
            showFallbackLoading={showFallbackLoading}
            bannerReady={bannerReady}
            twitterProfileUrl={twitterProfileUrl}
            followState={followState}
            metrics={metrics}
            locationIsLinkable={locationIsLinkable}
            renderAffiliationPreview={previewWrapper}
            formatJoinedAt={formatJoinedAt}
          />
        ) : null}
      </HoverCardContent>
    </HoverCard>
  );
}
